import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Plus } from "lucide-react";
import { EmSwash } from "../EmSwash";
import { NoteEyebrow } from "../NoteEyebrow";
import { FAQ_GROUPS, groupOf } from "./faqGroups";
import { TILE_BOUNCE, useTapBounce } from "../../lib/tapBounce";

/** One of the three category cards. The tinted slab slides between cards; the swash draws itself in. */
export const CategoryCard = ({ g, active, onClick }) => {
  const Icon = g.icon;
  const [state, onTap] = useTapBounce();
  return (
    <motion.button
      type="button"
      onClick={onClick}
      onTap={onTap}
      aria-pressed={active}
      data-testid={`faq-group-${g.key}`}
      initial="rest"
      whileHover="hover"
      animate={state}
      className={`group relative flex w-full flex-col items-start overflow-hidden rounded-2xl border-2 bg-bone p-6 text-left transition-colors lg:p-7 ${
        active ? g.border : "border-ink/10 hover:border-ink/30"
      }`}
    >
      {active && (
        <motion.span
          layoutId="faq-active-slab"
          aria-hidden="true"
          className="absolute inset-0"
          initial={false}
          animate={{ backgroundColor: g.tint }}
          transition={{ type: "spring", stiffness: 420, damping: 38 }}
        />
      )}
      <span className="relative flex w-full items-start justify-between">
        <NoteEyebrow className="mt-1">{g.note}</NoteEyebrow>
        <motion.span
          className="inline-flex"
          variants={{ rest: { y: 0 }, hover: { y: -5 }, bounce: TILE_BOUNCE }}
          transition={{ type: "spring", stiffness: 420, damping: 14, mass: 0.6 }}
          data-testid={`faq-group-${g.key}-tile`}
        >
          <motion.span
            className={`tile-clay inline-flex h-11 w-11 items-center justify-center rounded-xl ${g.bg} text-sand`}
            animate={active ? { rotate: [0, -8, 6, 0], scale: [1, 1.06, 1] } : { rotate: 0, scale: 1 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
          >
            <Icon className="h-5 w-5" />
          </motion.span>
        </motion.span>
      </span>
      <span className="relative mt-5 font-serif text-3xl leading-none text-ink">
        {active ? <EmSwash color={g.text} draw>{g.title}</EmSwash> : g.title}
      </span>
      {g.blurb && <span className="relative mt-4 text-[13px] leading-relaxed text-olive">{g.blurb}</span>}
    </motion.button>
  );
};

/** A question row: colour marker, no numerals; answer gets the group's rule. */
export const QuestionRow = ({ f, i, open, onToggle, showGroup = false }) => {
  const g = groupOf(f.group);
  return (
    <motion.li
      layout
      id={f.id}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -6 }}
      transition={{ duration: 0.28, delay: Math.min(i, 8) * 0.045 }}
      className="scroll-mt-28 border-b border-ink/10"
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        data-testid={`faq-row-${f.group}-${i}`}
        className="group flex w-full items-start gap-5 py-6 text-left lg:gap-7 lg:py-7"
      >
        <span
          className={`mt-1.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full border-2 transition-all duration-300 ${
            open ? `${g.bg} ${g.border} text-sand rotate-45` : `${g.border} ${g.text} group-hover:scale-110`
          }`}
        >
          <Plus className="h-3.5 w-3.5" strokeWidth={2.4} />
        </span>
        <span className="flex-1">
          {showGroup && (
            <span className={`mb-1 block text-[13px] font-semibold uppercase tracking-[0.22em] ${g.text}`}>{g.title}</span>
          )}
          <span className="block font-serif text-3xl leading-tight text-ink transition-transform duration-300 group-hover:translate-x-1">{f.q}</span>
        </span>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.28 }}
            className="overflow-hidden"
          >
            <div className={`mb-7 ml-[3.25rem] max-w-2xl border-l-[3px] pl-5 text-base leading-relaxed text-olive lg:ml-[3.75rem] ${g.border}`}>
              {f.a}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.li>
  );
};

/** Category switcher + ledger. `initialOpen` = index of the question to start expanded (deep links). */
export const CategoryBrowser = ({ items, activeKey, onPick, initialOpen = 0 }) => {
  const g = groupOf(activeKey);
  const [open, setOpen] = useState(initialOpen);
  return (
    <>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3 lg:gap-6" data-testid="faq-groups">
        {FAQ_GROUPS.map((x) => (
          <CategoryCard key={x.key} g={x} active={x.key === activeKey} onClick={() => { onPick(x.key); setOpen(0); }} />
        ))}
      </div>
      <div className="mt-14 grid grid-cols-1 gap-10 lg:mt-20 lg:grid-cols-12 lg:gap-16">
        <div className="lg:col-span-3">
          <AnimatePresence mode="wait">
            <motion.div key={activeKey} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 8 }} transition={{ duration: 0.22 }}>
              <NoteEyebrow>{g.note}</NoteEyebrow>
              <h2 className="mt-3 font-serif text-3xl leading-tight text-ink">{g.title}</h2>
              {g.blurb && <p className="mt-3 text-base leading-relaxed text-olive">{g.blurb}</p>}
            </motion.div>
          </AnimatePresence>
        </div>
        <div className="lg:col-span-9">
          <AnimatePresence mode="wait">
            <motion.ul key={activeKey} className="border-t border-ink/10" data-testid="faq-ledger" exit={{ opacity: 0 }}>
              {items.map((f, i) => (
                <QuestionRow key={f.q} f={f} i={i} open={open === i} onToggle={() => setOpen(open === i ? -1 : i)} />
              ))}
            </motion.ul>
          </AnimatePresence>
        </div>
      </div>
    </>
  );
};
